import { getPublicMediaRoot } from '../../serviceAgent';
import { UserProfileType } from '../../../peekapak-types/DataProtocolTypes';
import Documents from '../../Documents';

interface LibraryBannerType {
  filterTag: string;
  level?: string[];
  title: string;
  subtitle?: string; // Added for Takween banner
  content: string;
  months: number[]; //Added so it shows banner by their months (filtering)
  imgURL?: string; // Made optional to support logoURLs
  logoURLs?: string[]; // Added for multiple logos (Takween)
  menaURL?: string;
  imgAlt?: string; // Made optional since logoURLs don't need alt text
  keyRequired: string[];
  applicationTags?: string[]; //Added so it filters mena and not mena
  buttons?: {
    type: string;
    text: string;
    linkLocation?: string | undefined;
    assetId?: string | undefined;
    filterValue?: string | undefined;
  }[];
}

// Custom banner configuration for specific license levels and filters
// This allows overriding banners for specific user types and filter combinations
interface CustomBannerConfig {
  licenseLevel: string;
  filterTag: string;
  level?: string;
  banner: LibraryBannerType;
}

// Extensible banner configurations by license level and filter
// Add new license levels and their custom banners here
const CUSTOM_BANNER_CONFIGS: CustomBannerConfig[] = [
  // Takween-specific banners
  {
    licenseLevel: 'TAKWEEN',
    filterTag: '',
    banner: {
      filterTag: '',
      title: "Welcome to Peekapak's Building Resilience and Wellbeing Course",
      subtitle: 'Part of the Takween Al Ghurair Program',
      content:
        'As a Peekapak Facilitator, you have full access to two editions of the course:\n• In-Class Edition – designed for direct classroom delivery\n• After-School Edition – tailored for extended learning programs',
      months: [],
      logoURLs: ['/images/logos/peekapak-vertical-logo.png', '/images/logos/agf-logo.png'],
      keyRequired: ['KEY_MANAGE_STUDENTS'],
      buttons: [
        {
          type: 'libraryFilterLink',
          text: 'IN-CLASS COURSE',
          filterValue: 'middleSchool',
        },
        {
          type: 'libraryFilterLink',
          text: 'AFTER-SCHOOL COURSE',
          filterValue: 'highSchool',
        },
      ],
    },
  },
  {
    licenseLevel: 'TAKWEEN',
    filterTag: 'middleSchool',
    banner: {
      filterTag: 'middleSchool',
      title: 'Takween Middle School',
      content:
        'Discover age-appropriate social emotional learning content for grades 6-8. Our Takween middle school units help students develop emotional awareness, build resilience, explore cultural identity, and practice mindful communication.',
      months: [],
      imgURL: `${getPublicMediaRoot()}images/thumbnails/library/takweenMiddleSchoolBanner.png`,
      imgAlt: 'Takween Middle School Students',
      keyRequired: ['KEY_MANAGE_STUDENTS'],
      buttons: [
        {
          type: 'libraryFilterLink',
          text: 'View All Takween Content',
          filterValue: '',
        },
      ],
    },
  },
  {
    licenseLevel: 'TAKWEEN',
    filterTag: 'highSchool',
    banner: {
      filterTag: 'highSchool',
      title: 'Takween High School',
      content:
        'Engage older students with advanced social emotional learning concepts for grades 9-12. Our Takween high school units prepare students for global citizenship while honoring their cultural heritage.',
      months: [],
      imgURL: `${getPublicMediaRoot()}images/thumbnails/library/takweenHighSchoolBanner.png`,
      imgAlt: 'Takween High School Students',
      keyRequired: ['KEY_MANAGE_STUDENTS'],
      buttons: [
        {
          type: 'libraryFilterLink',
          text: 'View All Takween Content',
          filterValue: '',
        },
      ],
    },
  },
  // Add other custom banner configurations here as needed
  // Example for other license types:
  // {
  //   licenseLevel: 'ELEMENTARY_ONLY',
  //   filterTag: '',
  //   banner: { /* custom banner for elementary-only users */ }
  // },
];

export const banners: LibraryBannerType[] = [
  //BASIC
  {
    filterTag: '',
    title: 'Welcome to Peekapak',
    content:
      "As a BASIC user, you get access to one level of each unit's books, one introductory lesson on each wellbeing topic, 10 illustrated posters and embedded teacher PD on each topic. We recommend starting with our Self-Regulation unit!",
    months: [], //For all months
    imgURL: `${getPublicMediaRoot()}images/thumbnails/library/welcomeBanner.png`,
    menaURL: `${getPublicMediaRoot()}images/thumbnails/library/welcomeBannerUAE.png`,
    imgAlt: 'myPeekaville Characters',
    keyRequired: ['KEY_BASIC'],
    applicationTags: [],
    buttons: [
      {
        type: 'videoLink',
        text: 'Watch our How To Video',
        linkLocation: '',
        assetId: 'RJcA015qnttGnOhIDRxcnv36R02KbO5zOVw69cZ2JwhMM',
      },
      {
        type: 'internalLinkPush',
        text: 'Upgrade Me',
        linkLocation: '/activateTrial',
      },
    ],
  },
  {
    filterTag: 'freemium',
    level: ['EL'],
    title: 'Welcome to Peekapak! BASIC Edition',
    content:
      'Thanks for supporting the minds and hearts of children through Peekapak! With a BASIC account, you get access to the first lesson of each unit, one level of storybook (I-J), 1 video lesson, 1 music lesson, unit posters and fun wellbeing extras!',
    months: [], //For all months
    imgURL: `${getPublicMediaRoot()}images/thumbnails/library/welcomeBanner.png`,
    menaURL: `${getPublicMediaRoot()}images/thumbnails/library/welcomeBannerUAE.png`,
    imgAlt: 'myPeekaville Characters',
    keyRequired: ['KEY_BASIC'],
    applicationTags: [],
    buttons: [
      {
        type: 'videoLink',
        text: 'Getting Started',
        linkLocation: '',
        assetId: 'YR02VoNJ92iEOHycQZwWlerYA01aLuc55utyp02J1KgSvI',
      },
      {
        type: 'internalLinkPush',
        text: 'Upgrade Me',
        linkLocation: '/features',
      },
    ],
  },
  {
    filterTag: 'freemium',
    level: ['MS'],
    title: 'Welcome to Peekapak! BASIC Edition',
    content:
      'Thanks for supporting the minds and hearts of children through Peekapak! With a BASIC account, you get partial access to our Stress and Regulation unit.',
    months: [], //For all months
    imgURL: `${getPublicMediaRoot()}images/thumbnails/library/welcomeBanner-secondary.png`,
    imgAlt: 'middle school students',
    keyRequired: ['KEY_BASIC'],
    applicationTags: [],
    buttons: [
      {
        type: 'videoLink',
        text: 'Getting Started',
        linkLocation: '',
        assetId: 'rcy1cyiZVtaffOPZXx9k02secOrLxo0000OVa02WItcxCBA',
      },
      {
        type: 'internalLinkPush',
        text: 'Upgrade Me',
        linkLocation: '/features',
      },
    ],
  },
  {
    filterTag: 'freemium',
    level: ['HS'],
    title: 'Welcome to Peekapak! BASIC Edition',
    content:
      'Thanks for supporting the minds and hearts of children through Peekapak! With a BASIC account, you get access to lesson 1 of each unit.',
    months: [], //For all months
    imgURL: `${getPublicMediaRoot()}images/thumbnails/library/welcomeBanner-high-school.png`,
    imgAlt: 'high school students',
    keyRequired: ['KEY_BASIC'],
    applicationTags: [],
    buttons: [
      {
        type: 'internalLinkPush',
        text: 'Upgrade Me',
        linkLocation: '/features',
      },
    ],
  },

  //NOT-MENA, PK-5
  {
    filterTag: '',
    level: ['EL'],
    title: "Back to School? We've Got You Covered.",
    content:
      'Jumpstart the year with our free Educator Toolkit - packed with ready-to-go lessons, classroom posters, and joyful activities that build wellbeing from day one.',
    months: [7, 8], //August=7, September=8, etc.
    imgURL: `${getPublicMediaRoot()}images/thumbnails/library/august-banner.png`,
    imgAlt: 'myPeekaville Characters',
    keyRequired: ['KEY_ALL_LESSON_PLANS'],
    applicationTags: ['not-mena'],
    buttons: [
      {
        type: 'videoLink',
        text: 'Download your toolkit',
        linkLocation: '',
        assetId: 'YR02VoNJ92iEOHycQZwWlerYA01aLuc55utyp02J1KgSvI',
      },
    ],
  },
  {
    filterTag: '',
    level: ['EL'],
    title: 'Get your team started with our Implementation Plan',
    content:
      'Let us help you define your wellbeing vision, team and develop a custom Peekapak plan for the school year!',
    months: [7, 8], //August=7, September=8, etc.
    imgURL: `${getPublicMediaRoot()}images/thumbnails/library/august-banner.png`,
    imgAlt: 'myPeekaville Characters',
    keyRequired: ['KEY_MANAGE_STUDENTS'],
    applicationTags: ['not-mena'],
    buttons: [
      {
        type: 'internalLinkWindow',
        text: 'Download your Implementation Plan',
        linkLocation: '', //needs to be added
        assetId: '',
      },
    ],
  },
  {
    filterTag: '',
    level: ['EL'],
    title: 'October is Anti-Bullying Month',
    content:
      "Help students build respect, empathy, and belonging with Peekapak's Respect unit. Foster open conversations and a safe, supportive classroom all month long.",
    months: [9], //October
    imgURL: `${getPublicMediaRoot()}images/thumbnails/library/october-banner-na.png`,
    imgAlt: 'myPeekaville Characters',
    keyRequired: ['KEY_ALL_LESSON_PLANS'],
    applicationTags: ['not-mena'],
    buttons: [],
  },
  {
    filterTag: '',
    level: ['EL'],
    title: 'Monitor Student Confidence and Belonging',
    content:
      'Check in on wellbeing progress with real-time data. Support targeted interventions and share interventions with your team.',
    months: [9], //October
    imgURL: `${getPublicMediaRoot()}images/thumbnails/library/october-admin-banner.png`,
    imgAlt: 'myPeekaville Characters',
    keyRequired: ['KEY_MANAGE_STUDENTS'],
    applicationTags: ['not-mena'],
    buttons: [
      {
        type: 'internalLinkPush',
        text: 'View school dashboard',
        linkLocation: '/reports',
      },
    ],
  },
  {
    filterTag: '',
    level: ['EL'],
    title: 'Introducing the Teacher-Led Check-In Tool!',
    content:
      'Our brand new teacher-led check-in  is designed for quick, meaningful moments of connection—educators can now check-in with students in seconds, without requiring logins. Ideal for entrance or exit tickets, especially for early grades.',
    months: [10], //November
    imgURL: `${getPublicMediaRoot()}images/thumbnails/library/november-check-in.png`,
    imgAlt: 'myPeekaville Characters',
    keyRequired: ['KEY_ALL_LESSON_PLANS'],
    applicationTags: ['not-mena'],
    buttons: [
      {
        type: 'internalLinkPush',
        text: 'View Check-In Tool',
        linkLocation: '/teacherledcheckin',
      },
    ],
  },
  {
    filterTag: '',
    level: ['EL'],
    title: 'Introducing the Teacher-Led Check-In Tool!',
    content:
      'Our brand new teacher-led check-in  is designed for quick, meaningful moments of connection—educators can now check-in with students in seconds, without requiring logins. Ideal for entrance or exit tickets, especially for early grades.',
    months: [10], //November
    imgURL: `${getPublicMediaRoot()}images/thumbnails/library/november-check-in.png`,
    imgAlt: 'myPeekaville Characters',
    keyRequired: ['KEY_MANAGE_STUDENTS'],
    applicationTags: ['not-mena'],
    buttons: [
      {
        type: 'internalLinkPush',
        text: 'View Check-In Tool',
        linkLocation: '/teacherledcheckin',
      },
    ],
  },
  {
    filterTag: '',
    level: ['EL'],
    title: 'Reflect, Celebrate, Recharge',
    content:
      'Close out the year with joy! Celebrate classroom growth, reflect on wellbeing wins, and prep students for a restful break.',
    months: [11], //December
    imgURL: `${getPublicMediaRoot()}images/thumbnails/library/december-teacher-admin-banner.png`,
    imgAlt: 'myPeekaville Characters',
    keyRequired: ['KEY_ALL_LESSON_PLANS'],
    applicationTags: ['not-mena'],
    buttons: [
      {
        type: 'internalLinkWindow',
        text: 'Wrap up with reflection',
        linkLocation:
          'https://peekaplatform.s3.us-east-1.amazonaws.com/media30/resources/Reflect%2C+Celebrate%2C+Recharge+(PK-5).pdf',
      },
    ],
  },
  {
    filterTag: '',
    level: ['EL'],
    title: 'Reflect, Celebrate, Recharge',
    content:
      'Close out the year with joy! Celebrate classroom growth, reflect on wellbeing wins, and prep students for a restful break.',
    months: [11], //December
    imgURL: `${getPublicMediaRoot()}images/thumbnails/library/december-teacher-admin-banner.png`,
    imgAlt: 'myPeekaville Characters',
    keyRequired: ['KEY_MANAGE_STUDENTS'],
    applicationTags: ['not-mena'],
    buttons: [
      {
        type: 'internalLinkPush',
        text: 'View school dashboard',
        linkLocation: '/reports',
      },
    ],
  },
  {
    filterTag: '',
    level: ['EL'],
    title: 'Perseverance Starts Here',
    content:
      'New year, new goals! Empower students to keep going, even when learning gets tough - with engaging stories and lessons focused on perseverance.',
    months: [0], //Janurary
    imgURL: `${getPublicMediaRoot()}images/thumbnails/library/january-teacher-admin-banner.png`,
    imgAlt: 'myPeekaville Characters',
    keyRequired: ['KEY_ALL_LESSON_PLANS'],
    applicationTags: ['not-mena'],
    buttons: [
      {
        type: 'internalLinkWindow',
        text: 'Try the Perseverance Challenge',
        linkLocation: '', //needs to be added
        assetId: '',
      },
    ],
  },
  {
    filterTag: '',
    level: ['EL'],
    title: 'Start Strong with Perseverance Goals',
    content:
      'Encourage your educators to kick off the year with lessons that build grit and determination. Track student growth right from the dashboard.',
    months: [0], //Janurary
    imgURL: `${getPublicMediaRoot()}images/thumbnails/library/january-teacher-admin-banner.png`,
    imgAlt: 'myPeekaville Characters',
    keyRequired: ['KEY_MANAGE_STUDENTS'],
    applicationTags: ['not-mena'],
    buttons: [
      {
        type: 'internalLinkPush',
        text: 'View student progress',
        linkLocation: '/reports/MyPeekaville',
      },
    ],
  },
  {
    filterTag: '',
    level: ['EL'],
    title: 'Celebrate Friendship & Kindness',
    content:
      "From Valentine's Day to Pink Shirt Day, February's a perfect time to nurture empathy, inclusion, and kindness. We've got ready-to-use lessons to help!",
    months: [1], //February
    imgURL: `${getPublicMediaRoot()}images/thumbnails/library/february-teacher-banner.png`,
    imgAlt: 'myPeekaville Characters',
    keyRequired: ['KEY_ALL_LESSON_PLANS'],
    applicationTags: ['not-mena'],
    buttons: [
      {
        type: 'internalLinkWindow',
        text: "Download Valentine's Cards",
        linkLocation: 'https://s3.amazonaws.com/peekaplatform/media30/resources/Valentines_Cards.pdf',
      },
    ],
  },
  {
    filterTag: '',
    level: ['EL'],
    title: 'Highlight Growth with Mid-Year Insights',
    content:
      "Get ahead of end-of-year planning. Use Peekapak's data to spotlight progress in wellbeing and literacy across your classrooms",
    months: [1], //February
    imgURL: `${getPublicMediaRoot()}images/thumbnails/library/october-admin-banner.png`,
    imgAlt: 'myPeekaville Characters',
    keyRequired: ['KEY_MANAGE_STUDENTS'],
    applicationTags: ['not-mena'],
    buttons: [
      {
        type: 'internalLinkPush',
        text: 'View your reports',
        linkLocation: '/reports',
      },
    ],
  },
  {
    filterTag: '',
    level: ['EL'],
    title: 'Spread Joy with a Kindness Chain',
    content:
      'Kickstart a ripple of appreciation in your school! Have students write notes of encouragement, then link them into a growing kindness chain in the staffroom or hallway. A small gesture goes a long way.',
    months: [2], //March
    imgURL: `${getPublicMediaRoot()}images/thumbnails/library/march-teacher-banner.png`,
    imgAlt: 'myPeekaville Characters',
    keyRequired: ['KEY_ALL_LESSON_PLANS'],
    applicationTags: ['not-mena'],
    buttons: [
      {
        type: 'internalLinkWindow',
        text: 'Build your own chain',
        linkLocation: '', //needs to be added
        assetId: '',
      },
    ],
  },
  {
    filterTag: '',
    level: ['EL'],
    title: 'Support Staff and Student Wellbeing',
    content:
      'Help your team manage stress and stay grounded. Use our mindfulness and wellbeing resources to create a space for reflection this spring.',
    months: [2], //March
    imgURL: `${getPublicMediaRoot()}images/thumbnails/library/march-admin-banner.png`,
    imgAlt: 'myPeekaville Characters',
    keyRequired: ['KEY_MANAGE_STUDENTS'],
    applicationTags: ['not-mena'],
    buttons: [
      {
        type: 'internalLinkWindow',
        text: 'Explore PD Options',
        linkLocation: '', //needs to be added
        assetId: '',
      },
    ],
  },
  {
    filterTag: '',
    level: ['EL'],
    title: 'Celebrate Earth Day with Purpose 🌍',
    content:
      "This April, connect wellbeing with caring for our planet. Use Peekapak's activities to spark student curiosity, responsibility, and respect for the world around them.",
    months: [3], //April
    imgURL: `${getPublicMediaRoot()}images/thumbnails/library/april-teacher-admin-banner.png`,
    imgAlt: 'myPeekaville Characters',
    keyRequired: ['KEY_ALL_LESSON_PLANS'],
    applicationTags: ['not-mena'],
    buttons: [
      {
        type: 'internalLinkWindow',
        text: 'Explore Earth Day Activities',
        linkLocation: 'https://peekaplatform.s3.amazonaws.com/media30/Cop28/Learning+from+Nature%2C+PK-2.pdf',
      },
    ],
  },
  {
    filterTag: '',
    level: ['EL'],
    title: 'Celebrate Earth Day with Purpose 🌍',
    content:
      "This April, connect wellbeing with caring for our planet. Use Peekapak's activities to spark student curiosity, responsibility, and respect for the world around them.",
    months: [3], //April
    imgURL: `${getPublicMediaRoot()}images/thumbnails/library/april-teacher-admin-banner.png`,
    imgAlt: 'myPeekaville Characters',
    keyRequired: ['KEY_MANAGE_STUDENTS'],
    applicationTags: ['not-mena'],
    buttons: [
      {
        type: 'internalLinkWindow',
        text: 'Explore Earth Day Activities',
        linkLocation: 'https://peekaplatform.s3.amazonaws.com/media30/Cop28/Learning+from+Nature%2C+PK-2.pdf',
      },
    ],
  },
  {
    filterTag: '',
    level: ['EL'],
    title: "It's Teacher Appreciation Month 💛",
    content:
      "We see you, educators. You're making magic every day. Peekapak is celebrating you with special gifts, time-saving resources, and classroom surprises.",
    months: [4], //May
    imgURL: `${getPublicMediaRoot()}images/thumbnails/library/may-teacher-admin-banner.png`,
    imgAlt: 'myPeekaville Characters',
    keyRequired: ['KEY_ALL_LESSON_PLANS'],
    applicationTags: ['not-mena'],
    buttons: [
      {
        type: 'internalLinkWindow',
        text: 'Enter Teacher Appreciation Giveaway',
        linkLocation: '', //needs to be added
        assetId: '',
      },
    ],
  },
  {
    filterTag: '',
    level: ['EL'],
    title: 'Celebrate Your Educators',
    content:
      "It's Teacher Appreciation Month! Send a thank you note and share special Peekapak resources to lift up your team.",
    months: [4], //May
    imgURL: `${getPublicMediaRoot()}images/thumbnails/library/may-teacher-admin-banner.png`,
    imgAlt: 'myPeekaville Characters',
    keyRequired: ['KEY_MANAGE_STUDENTS'],
    applicationTags: ['not-mena'],
    buttons: [
      {
        type: 'internalLinkWindow',
        text: 'Download Thank You Template',
        linkLocation: '', //needs to be added
        assetId: '',
      },
    ],
  },
  {
    filterTag: '',
    level: ['EL'],
    title: 'Summer-Ready Wellbeing Activities',
    content:
      'Keep the learning (and joy!) going all summer. Grab our free activities that help students reflect, recharge, and stay connected.',
    months: [5, 6], //June, July
    imgURL: `${getPublicMediaRoot()}images/thumbnails/library/june-teacher-banner.png`,
    imgAlt: 'myPeekaville Characters',
    keyRequired: ['KEY_ALL_LESSON_PLANS'],
    applicationTags: ['not-mena'],
    buttons: [
      {
        type: 'internalLinkWindow',
        text: 'Explore Summer Resources',
        linkLocation: 'https://blog.peekapak.com/2022/06/22/7-summer-social-emotional-learning-activities/',
      },
    ],
  },
  {
    filterTag: '',
    level: ['EL'],
    title: 'Review Your Year-End Wellbeing Insights',
    content:
      "June is the perfect time to reflect on your school's progress. Use Peekapak's data dashboards to celebrate student growth and plan for an even stronger year ahead.",
    months: [5, 6], //June, July
    imgURL: `${getPublicMediaRoot()}images/thumbnails/library/october-admin-banner.png`,
    imgAlt: 'myPeekaville Characters',
    keyRequired: ['KEY_MANAGE_STUDENTS'],
    applicationTags: ['not-mena'],
    buttons: [
      {
        type: 'internalLinkPush',
        text: 'View your year-end report',
        linkLocation: '/reports',
      },
    ],
  },

  //NOT-MENA & MENA, G6-12
  {
    filterTag: '',
    level: ['MS', 'HS'],
    title: "Back to School? We've Got You Covered.",
    content:
      'Jumpstart the year with our free Educator Toolkit - packed with ready-to-go lessons, classroom posters, and joyful activities that build wellbeing from day one.',
    months: [7, 8], //August=7, September=8, etc.
    imgURL: `${getPublicMediaRoot()}images/thumbnails/library/august-banner.png`,
    imgAlt: 'myPeekaville Characters',
    keyRequired: ['KEY_ALL_LESSON_PLANS'], //teacher
    applicationTags: [],
    buttons: [
      {
        type: 'videoLink',
        text: 'Download your toolkit',
        linkLocation: '',
        assetId: 'YR02VoNJ92iEOHycQZwWlerYA01aLuc55utyp02J1KgSvI',
      },
    ],
  },
  {
    filterTag: '',
    level: ['MS', 'HS'],
    title: 'Get your team started with our Implementation Plan',
    content:
      'Let us help you define your wellbeing vision, team and develop a custom Peekapak plan for the school year!',
    months: [7, 8], //August=7, September=8, etc.
    imgURL: `${getPublicMediaRoot()}images/thumbnails/library/august-banner.png`,
    imgAlt: 'myPeekaville Characters',
    keyRequired: ['KEY_MANAGE_STUDENTS'],
    applicationTags: [],
    buttons: [
      {
        type: 'videoLink',
        text: 'Download your Implementation Plan',
        linkLocation: '',
        assetId: 'YR02VoNJ92iEOHycQZwWlerYA01aLuc55utyp02J1KgSvI',
      },
    ],
  },
  {
    filterTag: '',
    level: ['MS', 'HS'],
    title: 'October is Anti-Bullying Month',
    content:
      "Use Peekapak's Belonging lessons to help students navigate peer pressure and digital drama with empathy and confidence.",
    months: [9], //October
    imgURL: `${getPublicMediaRoot()}images/thumbnails/library/hs-october-teacher-admin-banner.png`,
    imgAlt: 'myPeekaville Characters',
    keyRequired: ['KEY_ALL_LESSON_PLANS'],
    applicationTags: [],
    buttons: [],
  },
  {
    filterTag: '',
    level: ['MS', 'HS'],
    title: 'Monitor Student Confidence and Belonging',
    content:
      'Check in on wellbeing progress with real-time data. Support targeted interventions and share interventions with your team.',
    months: [9], //October
    imgURL: `${getPublicMediaRoot()}images/thumbnails/library/hs-october-teacher-admin-banner.png`,
    imgAlt: 'myPeekaville Characters',
    keyRequired: ['KEY_MANAGE_STUDENTS'],
    applicationTags: [],
    buttons: [
      {
        type: 'internalLinkPush',
        text: 'View school dashboards',
        linkLocation: '/reports',
      },
    ],
  },
  {
    filterTag: '',
    level: ['MS', 'HS'],
    title: 'Join the Gratitude Challenge!',
    content:
      'Spark connection and gratitude all month long. Create your own Tree of Gratitude and/or gratitude tags, and send it to hello@peekapak.com for a chance to win!',
    months: [10], //November
    imgURL: `${getPublicMediaRoot()}images/thumbnails/library/hs-november-teacher-admin-banner.png`,
    imgAlt: 'myPeekaville Characters',
    keyRequired: ['KEY_ALL_LESSON_PLANS'],
    applicationTags: [],
    buttons: [
      {
        type: 'internalLinkWindow',
        text: 'Start the challenge',
        linkLocation: 'https://blog.peekapak.com/2025/10/23/gratitude-challenge-for-grades-6-12/',
      },
    ],
  },
  {
    filterTag: '',
    level: ['MS', 'HS'],
    title: 'Lead the Gratitude Challenge Schoolwide',
    content:
      'Foster connection across classrooms with our Gratitude Challenge. Invite staff to reflect and celebrate small wins.',
    months: [10], //November
    imgURL: `${getPublicMediaRoot()}images/thumbnails/library/hs-november-teacher-admin-banner.png`,
    imgAlt: 'myPeekaville Characters',
    keyRequired: ['KEY_MANAGE_STUDENTS'],
    applicationTags: [],
    buttons: [
      {
        type: 'internalLinkWindow',
        text: 'Start the challenge',
        linkLocation: 'https://blog.peekapak.com/2025/10/23/gratitude-challenge-for-grades-6-12/',
      },
    ],
  },
  {
    filterTag: '',
    level: ['MS', 'HS'],
    title: 'Reflect, Celebrate, Recharge',
    content:
      'Close out the year with joy! Celebrate classroom growth, reflect on wellbeing wins, and prep students for a restful break.',
    months: [11], //December
    imgURL: `${getPublicMediaRoot()}images/thumbnails/library/hs-december-teacher-admin-banner.png`,
    imgAlt: 'myPeekaville Characters',
    keyRequired: ['KEY_ALL_LESSON_PLANS'],
    applicationTags: ['not-mena'],
    buttons: [
      {
        type: 'internalLinkWindow',
        text: 'Wrap up with reflection',
        linkLocation:
          'https://peekaplatform.s3.us-east-1.amazonaws.com/media30/resources/Reflect%2C+Celebrate%2C+Recharge+(G6-12).pdf',
      },
    ],
  },
  {
    filterTag: '',
    level: ['MS', 'HS'],
    title: 'Reflect, Celebrate, Recharge',
    content:
      'Close out the year with joy! Celebrate classroom growth, reflect on wellbeing wins, and prep students for a restful break.',
    months: [11], //December
    imgURL: `${getPublicMediaRoot()}images/thumbnails/library/hs-december-teacher-admin-banner.png`,
    imgAlt: 'myPeekaville Characters',
    keyRequired: ['KEY_MANAGE_STUDENTS'],
    applicationTags: ['not-mena'],
    buttons: [
      {
        type: 'internalLinkPush',
        text: 'View school dashboard',
        linkLocation: '/reports',
      },
    ],
  },
  {
    filterTag: '',
    level: ['MS', 'HS'],
    title: 'Perseverance Starts Here',
    content:
      'Reignite motivation after the break with a challenge on grit and growth mindset-designed to help teens push through challenges and setbacks.',
    months: [0], //Janurary
    imgURL: `${getPublicMediaRoot()}images/thumbnails/library/hs-january-teacher-admin-banner.png`,
    imgAlt: 'myPeekaville Characters',
    keyRequired: ['KEY_ALL_LESSON_PLANS'],
    applicationTags: [],
    buttons: [
      {
        type: 'internalLinkWindow',
        text: 'Try the Perseverance Challenge',
        linkLocation: '', //needs to be added
        assetId: '',
      },
    ],
  },
  {
    filterTag: '',
    level: ['MS', 'HS'],
    title: 'Perseverance Starts Here',
    content:
      'Reignite motivation after the break with a challenge on grit and growth mindset-designed to help teens push through challenges and setbacks.',
    months: [0], //Janurary
    imgURL: `${getPublicMediaRoot()}images/thumbnails/library/hs-january-teacher-admin-banner.png`,
    imgAlt: 'myPeekaville Characters',
    keyRequired: ['KEY_MANAGE_STUDENTS'],
    applicationTags: [],
    buttons: [
      {
        type: 'internalLinkWindow',
        text: 'Try the Perseverance Challenge',
        linkLocation: '', //needs to be added
        assetId: '',
      },
    ],
  },
  {
    filterTag: '',
    level: ['MS', 'HS'],
    title: 'Celebrate Friendship & Kindness',
    content:
      "From Valentine's Day to Pink Shirt Day, February's a perfect time to nurture empathy, inclusion, and kindness. We've got ready-to-use lessons to help!",
    months: [1], //February
    imgURL: `${getPublicMediaRoot()}images/thumbnails/library/hs-february-teacher-banner.png`,
    imgAlt: 'myPeekaville Characters',
    keyRequired: ['KEY_ALL_LESSON_PLANS'],
    applicationTags: ['not-mena'],
    buttons: [
      {
        type: 'internalLinkWindow',
        text: "Download Valentine's Cards",
        linkLocation: 'https://s3.amazonaws.com/peekaplatform/media30/resources/Valentines_Cards.pdf',
      },
    ],
  },
  {
    filterTag: '',
    level: ['MS', 'HS'],
    title: 'Highlight Growth with Mid-Year Insights',
    content:
      "Get ahead of end-of-year planning. Use Peekapak's data to spotlight progress in wellbeing and literacy across your classrooms",
    months: [1], //February
    imgURL: `${getPublicMediaRoot()}images/thumbnails/library/october-admin-banner.png`,
    imgAlt: 'myPeekaville Characters',
    keyRequired: ['KEY_MANAGE_STUDENTS'],
    applicationTags: [],
    buttons: [
      {
        type: 'internalLinkPush',
        text: 'View your school report',
        linkLocation: '/reports',
      },
    ],
  },
  {
    filterTag: '',
    level: ['MS', 'HS'],
    title: 'Start a Kindness Chain',
    content:
      'Challenge students to write anonymous shoutouts, peer compliments, or appreciation tags to hang in your classroom or hallway.',
    months: [2], //March
    imgURL: `${getPublicMediaRoot()}images/thumbnails/library/march-teacher-banner.png`,
    imgAlt: 'myPeekaville Characters',
    keyRequired: ['KEY_ALL_LESSON_PLANS'],
    applicationTags: ['not-mena'],
    buttons: [
      {
        type: 'internalLinkWindow',
        text: 'Build your own chain',
        linkLocation: '', //needs to be added
        assetId: '',
      },
    ],
  },
  {
    filterTag: '',
    level: ['MS', 'HS'],
    title: 'Support Staff and Student Wellbeing',
    content:
      'Help your team manage stress and stay grounded. Use our mindfulness and wellbeing resources to create a space for reflection this spring.',
    months: [2], //March
    imgURL: `${getPublicMediaRoot()}images/thumbnails/library/march-admin-banner.png`,
    imgAlt: 'myPeekaville Characters',
    keyRequired: ['KEY_MANAGE_STUDENTS'],
    applicationTags: [],
    buttons: [
      {
        type: 'internalLinkPush',
        text: 'Explore PD Options',
        linkLocation: '/professionaldevelopment',
      },
    ],
  },
  {
    filterTag: '',
    level: ['MS', 'HS'],
    title: 'Celebrate Earth Day with Purpose 🌍',
    content:
      "This April, connect wellbeing with caring for our planet. Use Peekapak's activities to spark student curiosity, responsibility, and respect for the world around them.",
    months: [3], //April
    imgURL: `${getPublicMediaRoot()}images/thumbnails/library/hs-april-teacher-admin-banner.png`,
    imgAlt: 'myPeekaville Characters',
    keyRequired: ['KEY_ALL_LESSON_PLANS'],
    applicationTags: ['not-mena'],
    buttons: [
      {
        type: 'internalLinkWindow',
        text: 'Explore Earth Day Activities',
        linkLocation:
          'https://peekaplatform.s3.amazonaws.com/media30/Cop28/Climate+Change+Anxiety+and+Action+Lesson%2C+G9-12.pdf',
      },
    ],
  },
  {
    filterTag: '',
    level: ['MS', 'HS'],
    title: 'Celebrate Earth Day with Purpose 🌍',
    content:
      "This April, connect wellbeing with caring for our planet. Use Peekapak's activities to spark student curiosity, responsibility, and respect for the world around them.",
    months: [3], //April
    imgURL: `${getPublicMediaRoot()}images/thumbnails/library/hs-april-teacher-admin-banner.png`,
    imgAlt: 'myPeekaville Characters',
    keyRequired: ['KEY_MANAGE_STUDENTS'],
    applicationTags: ['not-mena'],
    buttons: [
      {
        type: 'internalLinkWindow',
        text: 'Explore Earth Day Activities',
        linkLocation:
          'https://peekaplatform.s3.amazonaws.com/media30/Cop28/Climate+Change+Anxiety+and+Action+Lesson%2C+G9-12.pdf',
      },
    ],
  },
  {
    filterTag: '',
    level: ['MS', 'HS'],
    title: "It's Teacher Appreciation Month 💛",
    content:
      "We see you, educators. You're making magic every day. Peekapak is celebrating you with special gifts, time-saving resources, and classroom surprises.",
    months: [4], //May
    imgURL: `${getPublicMediaRoot()}images/thumbnails/library/hs-may-teacher-admin-banner.png`,
    imgAlt: 'myPeekaville Characters',
    keyRequired: ['KEY_ALL_LESSON_PLANS'],
    applicationTags: [],
    buttons: [
      {
        type: 'internalLinkWindow',
        text: 'Enter Teacher Appreciation Giveaway',
        linkLocation: '', //needs to be added
        assetId: '',
      },
    ],
  },
  {
    filterTag: '',
    level: ['MS', 'HS'],
    title: 'Celebrate Your Educators',
    content:
      "It's Teacher Appreciation Month! Send a thank you note and share special Peekapak resources to lift up your team.",
    months: [4], //May
    imgURL: `${getPublicMediaRoot()}images/thumbnails/library/hs-may-teacher-admin-banner.png`,
    imgAlt: 'myPeekaville Characters',
    keyRequired: ['KEY_MANAGE_STUDENTS'],
    applicationTags: [],
    buttons: [
      {
        type: 'internalLinkWindow',
        text: 'Download Thank You Template',
        linkLocation: '', //needs to be added
        assetId: '',
      },
    ],
  },
  {
    filterTag: '',
    level: ['MS', 'HS'],
    title: 'Summer-Ready Wellbeing Activities',
    content:
      'Keep the learning (and joy!) going all summer. Grab our free activities that help students reflect, recharge, and stay connected.',
    months: [5, 6], //June, July
    imgURL: `${getPublicMediaRoot()}images/thumbnails/library/hs-june-teacher-banner.png`,
    imgAlt: 'myPeekaville Characters',
    keyRequired: ['KEY_ALL_LESSON_PLANS'],
    applicationTags: ['not-mena'],
    buttons: [
      {
        type: 'internalLinkWindow',
        text: '☀️Explore summer resources',
        linkLocation: '', //needs to be added
        assetId: '',
      },
    ],
  },
  {
    filterTag: '',
    level: ['MS', 'HS'],
    title: 'Review Your Year-End Wellbeing Insights',
    content:
      "June is the perfect time to reflect on your school's progress. Use Peekapak's data dashboards to celebrate student growth and plan for an even stronger year ahead.",
    months: [5, 6], //June, July
    imgURL: `${getPublicMediaRoot()}images/thumbnails/library/october-admin-banner.png`,
    imgAlt: 'myPeekaville Characters',
    keyRequired: ['KEY_MANAGE_STUDENTS'],
    applicationTags: ['not-mena'],
    buttons: [
      {
        type: 'internalLinkPush',
        text: 'View your year-end report',
        linkLocation: '/reports',
      },
    ],
  },

  //MENA, PK-5
  {
    filterTag: '',
    level: ['EL'],
    title: "Back to School? We've Got You Covered!",
    content:
      'Set the tone for a meaningful year. Use our Implementation Plan to align your team around wellbeing goals, UAE moral education values, and classroom success.',
    months: [7, 8], //August=7, September=8, etc.
    imgURL: `${getPublicMediaRoot()}images/thumbnails/library/august-banner.png`,
    imgAlt: 'myPeekaville Characters',
    keyRequired: ['KEY_ALL_LESSON_PLANS'],
    applicationTags: ['mena'],
    buttons: [
      {
        type: 'videoLink',
        text: 'Download your toolkit',
        linkLocation: '',
        assetId: 'YR02VoNJ92iEOHycQZwWlerYA01aLuc55utyp02J1KgSvI',
      },
    ],
  },
  {
    filterTag: '',
    level: ['EL'],
    title: 'Get your team started with our Implementation Plan',
    content:
      'Let us help you define your wellbeing vision, team and develop a custom Peekapak plan for the school year!',
    months: [7, 8], //August=7, September=8, etc.
    imgURL: `${getPublicMediaRoot()}images/thumbnails/library/august-banner.png`,
    imgAlt: 'myPeekaville Characters',
    keyRequired: ['KEY_MANAGE_STUDENTS'],
    applicationTags: ['mena'],
    buttons: [
      {
        type: 'videoLink',
        text: 'Download Implementation Plan',
        linkLocation: '',
        assetId: 'YR02VoNJ92iEOHycQZwWlerYA01aLuc55utyp02J1KgSvI',
      },
    ],
  },
  {
    filterTag: '',
    level: ['EL'],
    title: 'October is Anti-Bullying Month',
    content:
      'Prepare for UAE Flag Day (Nov 3) with lessons on respect, unity, and belonging. Help students explore what it means to be part of a strong, caring community.',
    months: [9], //Octover
    imgURL: `${getPublicMediaRoot()}images/thumbnails/library/mena-october-teacher-banner.png`,
    imgAlt: 'myPeekaville Characters',
    keyRequired: ['KEY_ALL_LESSON_PLANS'],
    applicationTags: ['mena'],
    buttons: [],
  },
  {
    filterTag: '',
    level: ['EL'],
    title: 'Monitor Student Confidence and Belonging',
    content:
      'Check in on wellbeing progress with real-time data. Support targeted interventions and share interventions with your team.',
    months: [9], //October
    imgURL: `${getPublicMediaRoot()}images/thumbnails/library/october-admin-banner.png`,
    imgAlt: 'myPeekaville Characters',
    keyRequired: ['KEY_MANAGE_STUDENTS'],
    applicationTags: ['mena'],
    buttons: [
      {
        type: 'internalLinkPush',
        text: 'View school dashboards',
        linkLocation: '/reports',
      },
    ],
  },
  {
    filterTag: '',
    level: ['EL'],
    title: 'Introducing the Teacher-Led Check-In Tool!',
    content:
      'Our brand new teacher-led check-in  is designed for quick, meaningful moments of connection—educators can now check-in with students in seconds, without requiring logins. Ideal for entrance or exit tickets, especially for early grades.',
    months: [10], //November
    imgURL: `${getPublicMediaRoot()}images/thumbnails/library/november-check-in.png`,
    imgAlt: 'myPeekaville Characters',
    keyRequired: ['KEY_ALL_LESSON_PLANS'],
    applicationTags: ['mena'],
    buttons: [
      {
        type: 'internalLinkPush',
        text: 'View Check-In Tool',
        linkLocation: '/teacherledcheckin',
      },
    ],
  },
  {
    filterTag: '',
    level: ['EL'],
    title: 'Introducing the Teacher-Led Check-In Tool!',
    content:
      'Our brand new teacher-led check-in  is designed for quick, meaningful moments of connection—educators can now check-in with students in seconds, without requiring logins. Ideal for entrance or exit tickets, especially for early grades.',
    months: [10], //November
    imgURL: `${getPublicMediaRoot()}images/thumbnails/library/november-check-in.png`,
    imgAlt: 'myPeekaville Characters',
    keyRequired: ['KEY_MANAGE_STUDENTS'],
    applicationTags: ['mena'],
    buttons: [
      {
        type: 'internalLinkPush',
        text: 'View Check-In Tool',
        linkLocation: '/teacherledcheckin', //needs to be added
      },
    ],
  },
  {
    filterTag: '',
    level: ['EL'], //elementary
    title: 'Celebrate National Day with Heart 🇦🇪',
    content:
      'Help students understand the values of the UAE-respect, kindness, and unity. Use Peekapak stories and reflection activities to deepen their appreciation.',
    months: [11], //December
    imgURL: `${getPublicMediaRoot()}images/thumbnails/library/mena-november-teacher-admin-banner.png`,
    imgAlt: 'myPeekaville Characters',
    keyRequired: ['KEY_ALL_LESSON_PLANS'], //teacher
    applicationTags: ['mena'],
    buttons: [
      {
        type: 'internalLinkWindow',
        text: 'Wrap up with reflection',
        linkLocation:
          'https://peekaplatform.s3.us-east-1.amazonaws.com/media30/resources/Reflect%2C+Celebrate%2C+Recharge+(PK-5%2C+MENA).pdf',
      },
    ],
  },
  {
    filterTag: '',
    level: ['MS', 'HS'], //middle school and highschool
    title: 'Celebrate National Day with Heart 🇦🇪',
    content:
      'Help students understand the values of the UAE-respect, kindness, and unity. Use Peekapak stories and reflection activities to deepen their appreciation.',
    months: [11], //December
    imgURL: `${getPublicMediaRoot()}images/thumbnails/library/mena-november-teacher-admin-banner.png`,
    imgAlt: 'myPeekaville Characters',
    keyRequired: ['KEY_ALL_LESSON_PLANS'], //teacher
    applicationTags: ['mena'],
    buttons: [
      {
        type: 'internalLinkWindow',
        text: 'Wrap up with reflection',
        linkLocation:
          'https://peekaplatform.s3.us-east-1.amazonaws.com/media30/resources/Reflect%2C+Celebrate%2C+Recharge+(G6-12).pdf',
      },
    ],
  },
  {
    filterTag: '',
    level: [], //all grades
    title: 'Celebrate UAE National Day with Purpose 🇦🇪',
    content:
      "Support educators in delivering lessons that reflect the UAE's rich culture and values. Recognize how your school fosters unity and wellbeing.",
    months: [11], //December
    imgURL: `${getPublicMediaRoot()}images/thumbnails/library/mena-november-teacher-admin-banner.png`,
    imgAlt: 'myPeekaville Characters',
    keyRequired: ['KEY_MANAGE_STUDENTS'], //admin
    applicationTags: ['mena'],
    buttons: [
      {
        type: 'internalLinkPush',
        text: 'View school dashboard',
        linkLocation: '/reports',
      },
    ],
  },
  {
    filterTag: '',
    level: ['EL'],
    title: 'Perseverance Starts Here.',
    content:
      'New year, new goals! Empower students to keep going, even when learning gets tough - with engaging stories and lessons focused on perseverance.',
    months: [0], //January
    imgURL: `${getPublicMediaRoot()}images/thumbnails/library/january-teacher-admin-banner.png`,
    imgAlt: 'myPeekaville Characters',
    keyRequired: ['KEY_ALL_LESSON_PLANS'],
    applicationTags: ['mena'],
    buttons: [
      {
        type: 'internalLinkWindow',
        text: 'Try the Perseverance Challenge',
        linkLocation: '', //needs to be added
        assetId: '',
      },
    ],
  },
  {
    filterTag: '',
    level: ['EL'],
    title: 'Start Strong with Perseverance Goals',
    content:
      'Encourage your educators to kick off the year with lessons that build grit and determination. Track student growth right from the dashboard.',
    months: [0], //January
    imgURL: `${getPublicMediaRoot()}images/thumbnails/library/january-teacher-admin-banner.png`,
    imgAlt: 'myPeekaville Characters',
    keyRequired: ['KEY_MANAGE_STUDENTS'],
    applicationTags: ['mena'],
    buttons: [
      {
        type: 'internalLinkPush',
        text: 'View student progress',
        linkLocation: '/reports',
      },
    ],
  },
  {
    //NEEDS TO BE CHANGED
    filterTag: '',
    level: ['EL'],
    title: 'Celebrate Friendship & Kindness.',
    content:
      "From Valentine's Day to Pink Shirt Day, February's a perfect time to nurture empathy, inclusion, and kindness. We've got ready-to-use lessons to help!",
    months: [1], //February
    imgURL: `${getPublicMediaRoot()}images/thumbnails/library/welcomeBanner.png`,
    menaURL: `${getPublicMediaRoot()}images/thumbnails/library/welcomeBannerUAE.png`,
    imgAlt: 'myPeekaville Characters',
    keyRequired: ['KEY_ALL_LESSON_PLANS'],
    applicationTags: ['mena'],
    buttons: [
      {
        type: 'internalLinkWindow',
        text: "Download Valentine's Card",
        linkLocation: '', //needs to be added
        assetId: '',
      },
    ],
  },
  {
    filterTag: '',
    level: ['EL'],
    title: 'Highlight Growth with Mid-Year Insights',
    content:
      "Get ahead of end-of-year lanning. Use Peekapak's data to spotlight progress in wellbeieng and literacy across your classrooms",
    months: [1], //February
    imgURL: `${getPublicMediaRoot()}images/thumbnails/library/october-admin-banner.png`,
    imgAlt: 'myPeekaville Characters',
    keyRequired: ['KEY_MANAGE_STUDENTS'],
    applicationTags: ['mena'],
    buttons: [
      {
        type: 'internalLinkPush',
        text: 'View your school report',
        linkLocation: '/reports',
      },
    ],
  },
  {
    filterTag: '',
    level: ['EL'],
    title: 'Kindness Chain for Ramadan Prep',
    content:
      'Ramadan is coming - build empathy and generosity with a classroom kindness chain. Encourage students to do small acts of good each day.',
    months: [2], //March
    imgURL: `${getPublicMediaRoot()}images/thumbnails/library/march-teacher-banner.png`,
    imgAlt: 'myPeekaville Characters',
    keyRequired: ['KEY_ALL_LESSON_PLANS'],
    applicationTags: ['mena'],
    buttons: [
      {
        type: 'internalLinkWindow',
        text: 'Start your kindness chain', //needs to be changed
        linkLocation: '',
        assetId: '',
      },
    ],
  },
  {
    filterTag: '',
    level: ['EL'],
    title: 'Support Staff and Student Wellbeing',
    content:
      'Help your team manage stress and stay grounded. Use our mindfulness and wellbeing resources to create a space for reflection this spring.',
    months: [2], //March
    imgURL: `${getPublicMediaRoot()}images/thumbnails/library/march-admin-banner.png`,
    imgAlt: 'myPeekaville Characters',
    keyRequired: ['KEY_MANAGE_STUDENTS'],
    applicationTags: ['mena'],
    buttons: [
      {
        type: 'internalLinkWindow',
        text: 'Explore PD Options',
        linkLocation: '', //needs to be added
        assetId: '',
      },
    ],
  },
  {
    filterTag: '',
    level: ['EL'],
    title: 'Kindness Chain for Ramadan Prep',
    content:
      'Ramadan is coming - build empathy and generosity with a classroom kindness chain. Encourage students to do small acts of good each day.',
    months: [2], //March
    imgURL: `${getPublicMediaRoot()}images/thumbnails/library/mena-april-teacher-admin-banner.png`,
    imgAlt: 'myPeekaville Characters',
    keyRequired: ['KEY_ALL_LESSON_PLANS'],
    applicationTags: ['mena'],
    buttons: [
      {
        type: 'internalLinkWindow',
        text: 'Start your kindness chain',
        linkLocation: '', //needs to be added
        assetId: '',
      },
    ],
  },
  {
    filterTag: '',
    level: ['EL'],
    title: 'Support Staff and Student Wellbeing',
    content:
      'Help your team manage stress and stay grounded. Use our mindfulness and wellbeing resources to create a space for reflection this spring.',
    months: [2], //March
    imgURL: `${getPublicMediaRoot()}images/thumbnails/library/mena-april-teacher-admin-banner.png`,
    imgAlt: 'myPeekaville Characters',
    keyRequired: ['KEY_MANAGE_STUDENTS'],
    applicationTags: ['mena'],
    buttons: [
      {
        type: 'internalLinkWindow',
        text: 'Explore PD Options',
        linkLocation: '', //needs to be added
        assetId: '',
      },
    ],
  },
  {
    filterTag: '',
    level: ['EL'],
    title: 'Celebrate Ramadan with Reflection',
    content:
      'Support students during Ramadan with mindful lessons about empathy, patience, and community. Activities respect fasting and include calm, reflective option.',
    months: [3], //April
    imgURL: `${getPublicMediaRoot()}images/thumbnails/library/mena-april-teacher-admin-banner.png`,
    imgAlt: 'myPeekaville Characters',
    keyRequired: ['KEY_ALL_LESSON_PLANS'],
    applicationTags: ['mena'],
    buttons: [
      {
        type: 'internalLinkWindow',
        text: 'Get Ramadan resources',
        linkLocation: 'https://peekaplatform.s3.us-east-1.amazonaws.com/media30/resources/Ramadan+Tags_v1.pdf',
      },
    ],
  },
  {
    filterTag: '',
    level: ['EL'],
    title: 'Support Students During Ramadan 🌙',
    content:
      'Use low-energy, high-impact wellbeing lessons to support fasting students. Provide teachers with calming, reflective tools to use throughout the month.',
    months: [3], //April
    imgURL: `${getPublicMediaRoot()}images/thumbnails/library/mena-april-teacher-admin-banner.png`,
    imgAlt: 'myPeekaville Characters',
    keyRequired: ['KEY_MANAGE_STUDENTS'],
    applicationTags: ['mena'],
    buttons: [
      {
        type: 'internalLinkWindow',
        text: 'Share Ramadan Resources',
        linkLocation: 'https://peekaplatform.s3.us-east-1.amazonaws.com/media30/resources/Ramadan+Tags_v1.pdf',
      },
    ],
  },
  {
    filterTag: '',
    level: ['EL'],
    title: "It's Teacher Appreciation Month💛",
    content:
      "We see you, educators. You're making magic every day. Peekapak is celebrating you with special gifts, time-saving resources, and classroom surprises.",
    months: [4], //May
    imgURL: `${getPublicMediaRoot()}images/thumbnails/library/may-teacher-admin-banner.png`,
    imgAlt: 'myPeekaville Characters',
    keyRequired: ['KEY_ALL_LESSON_PLANS'],
    applicationTags: ['mena'],
    buttons: [
      {
        type: 'internalLinkWindow',
        text: 'Enter Teacher Appreciation Giveaway',
        linkLocation: '', //needs to be added
        assetId: '',
      },
    ],
  },
  {
    filterTag: '',
    level: ['EL'],
    title: 'Celebrate Your Educators',
    content:
      "It's Teacher Appreciation Month! Send a thank-you note and share special Peekapak resources to lift up your team.",
    months: [4], //May
    imgURL: `${getPublicMediaRoot()}images/thumbnails/library/may-teacher-admin-banner.png`,
    imgAlt: 'myPeekaville Characters',
    keyRequired: ['KEY_MANAGE_STUDENTS'],
    applicationTags: ['mena'],
    buttons: [
      {
        type: 'internalLinkWindow',
        text: 'Download Thank You Template',
        linkLocation: '', //needs to be added
        assetId: '',
      },
    ],
  },
  {
    filterTag: '',
    level: ['EL'],
    title: 'Summer-Ready Wellbeing Activities',
    content:
      'Keep the learning (and joy!) going all summer. Grab our free activities that help students reflect, recharge, and stay connected.',
    months: [5, 6], //June, July
    imgURL: `${getPublicMediaRoot()}images/thumbnails/library/mena-june-teacher-banner.png`,
    imgAlt: 'myPeekaville Characters',
    keyRequired: ['KEY_ALL_LESSON_PLANS'],
    applicationTags: ['mena'],
    buttons: [
      {
        type: 'internalLinkWindow',
        text: 'Explore summer resources',
        linkLocation: '', //needs to be added
        assetId: '',
      },
    ],
  },
  {
    filterTag: '',
    level: ['EL'],
    title: 'Review Your Year-End Wellbeing Insights',
    content:
      "June is the perfect time to reflect on your school's progress. Use Peekapak's data dashboards to celebrate student growth and plan for an even stronger year ahead.",
    months: [5, 6], //June, July
    imgURL: `${getPublicMediaRoot()}images/thumbnails/library/october-admin-banner.png`,
    imgAlt: 'myPeekaville Characters',
    keyRequired: ['KEY_MANAGE_STUDENTS'],
    applicationTags: ['mena'],
    buttons: [
      {
        type: 'internalLinkPush',
        text: 'View your year-end report',
        linkLocation: '/reports',
      },
    ],
  },

  //MENA, G6-12
  {
    filterTag: '',
    level: ['MS', 'HS'],
    title: 'Celebrate Friendship & Kindness',
    content:
      "February's a perfect time to nurture empathy, inclusion, and kindness. We've got ready-to-use lessons to help!",
    months: [1], //February
    imgURL: `${getPublicMediaRoot()}images/thumbnails/library/hs-february-teacher-banner.png`,
    imgAlt: 'myPeekaville Characters',
    keyRequired: ['KEY_ALL_LESSON_PLANS'],
    applicationTags: ['mena'],
    buttons: [
      {
        type: 'internalLinkWindow',
        text: '',
        linkLocation: '',
      },
    ],
  },
  {
    filterTag: '',
    level: ['MS', 'HS'],
    title: 'Start a Kindness Chain',
    content:
      'Have students write peer shoutours, compliments, or acts of good in anticipation of Ramadan. Display them in a hallway or shared space.',
    months: [2], //March
    imgURL: `${getPublicMediaRoot()}images/thumbnails/library/march-teacher-banner.png`,
    imgAlt: 'myPeekaville Characters',
    keyRequired: ['KEY_ALL_LESSON_PLANS'],
    applicationTags: ['mena'],
    buttons: [
      {
        type: 'internalLinkWindow',
        text: 'Build your own chain',
        linkLocation: '', //needs to be added
        assetId: '',
      },
    ],
  },
  {
    filterTag: '',
    level: ['MS', 'HS'],
    title: 'Support Students During Ramadan',
    content:
      'Offer calm, reflective wellbeing activities tailored for students who are fasting. Build patience, empathy, and mindfulness.',
    months: [3], //April
    imgURL: `${getPublicMediaRoot()}images/thumbnails/library/mena-april-teacher-admin-banner.png`,
    imgAlt: 'myPeekaville Characters',
    keyRequired: ['KEY_ALL_LESSON_PLANS'],
    applicationTags: ['mena'],
    buttons: [
      {
        type: 'internalLinkWindow',
        text: 'Explore Ramadan Resources',
        linkLocation: 'https://peekaplatform.s3.us-east-1.amazonaws.com/media30/resources/Ramadan+Tags_v1.pdf',
      },
    ],
  },
  {
    filterTag: '',
    level: ['MS', 'HS'],
    title: 'Summer-Ready Wellbeing Activities',
    content:
      'Use our reflection prompts, end-of-year activities, and summer wellbeing tools to help students close the year with pride and purpose.',
    months: [5, 6], //June, July
    imgURL: `${getPublicMediaRoot()}images/thumbnails/library/hs-june-teacher-banner.png`,
    imgAlt: 'myPeekaville Characters',
    keyRequired: ['KEY_ALL_LESSON_PLANS'],
    applicationTags: ['mena'],
    buttons: [
      {
        type: 'internalLinkWindow',
        text: '☀️Explore summer resources',
        linkLocation: '', //needs to be added
        assetId: '',
      },
    ],
  },
  {
    filterTag: '',
    level: ['MS', 'HS'],
    title: 'Review Your Year-End Wellbeing Insights',
    content:
      'Celebrate your progress and align plans for next year. Use data insights to reflect on student growth and connect with ADEK wellbeing goals.',
    months: [5, 6], //June, July
    imgURL: `${getPublicMediaRoot()}images/thumbnails/library/october-admin-banner.png`,
    imgAlt: 'myPeekaville Characters',
    keyRequired: ['KEY_MANAGE_STUDENTS'],
    applicationTags: ['mena'],
    buttons: [
      {
        type: 'internalLinkPush',
        text: 'View your year-end report',
        linkLocation: '/reports',
      },
    ],
  },

  //OTHERS
  {
    filterTag: 'elementary',
    title: 'Elementary School',
    content:
      'Peekapak provides 10 core units relating to social emotional learning competencies.  Each core unit has home activities for families and myPeekaville games for students to complete.',
    months: [],
    imgURL: `${getPublicMediaRoot()}images/thumbnails/library/elementaryBanner.png`,
    imgAlt: 'myPeekaville Characters',
    keyRequired: ['KEY_MANAGE_STUDENTS'],
    applicationTags: [],
    buttons: [
      {
        type: 'videoLink',
        text: 'Watch our how to video',
        linkLocation: '',
        assetId: 'YR02VoNJ92iEOHycQZwWlerYA01aLuc55utyp02J1KgSvI',
      },
    ],
  },
  {
    filterTag: 'elementary',
    title: 'Elementary School',
    content:
      'Peekapak provides 10 core units relating to social emotional learning competencies.  To get access to home activities and myPeekaville, please note this is available in our Peekapak PRO subscription.',
    months: [],
    imgURL: `${getPublicMediaRoot()}images/thumbnails/library/elementaryBanner.png`,
    imgAlt: 'myPeekaville Characters',
    keyRequired: ['KEY_ALL_LESSON_PLANS'],
    applicationTags: [],
    buttons: [
      {
        type: 'videoLink',
        text: 'Watch our how to video',
        linkLocation: '',
        assetId: 'YR02VoNJ92iEOHycQZwWlerYA01aLuc55utyp02J1KgSvI',
      },
    ],
  },
  {
    filterTag: 'elementary',
    title: 'Elementary School',
    content:
      'Peekapak provides 10 core units relating to social emotional learning competencies. Upgrade to Peekapak PRO to access all 80+ lessons and the associated home activities for families and myPeekaville games for students to complete.',
    months: [],
    imgURL: `${getPublicMediaRoot()}images/thumbnails/library/elementaryBanner.png`,
    imgAlt: 'myPeekaville Characters',
    keyRequired: ['KEY_BASIC'],
    applicationTags: [],
    buttons: [
      {
        type: 'videoLink',
        text: 'Watch our how to video',
        linkLocation: 'https://www.youtube.com/watch?v=kDVk_Dkae3U&t=1s',
        assetId: 'YR02VoNJ92iEOHycQZwWlerYA01aLuc55utyp02J1KgSvI',
      },
    ],
  },
  {
    filterTag: 'middleSchool',
    title: 'Middle School',
    content:
      'As a Secondary School Peekapak PRO user, you get full access to Peekapak’s lessons, activities, stories and challenges. We recommend starting with our Welcome to Peekapak unit!',
    months: [],
    imgURL: `${getPublicMediaRoot()}images/thumbnails/library/middleschoolBanner.png`,
    imgAlt: 'secondary school student waving',
    keyRequired: ['KEY_MANAGE_STUDENTS'],
    buttons: [
      {
        type: 'videoLink',
        text: 'Watch our how to video',
        linkLocation: '',
        assetId: 'rcy1cyiZVtaffOPZXx9k02secOrLxo0000OVa02WItcxCBA',
      },
    ],
  },
  {
    filterTag: 'middleSchool',
    title: 'Middle School',
    content:
      'As a secondary school Peekapak PRO user, you have access to all of Peekapak’s lessons, stories and teacher resources. We recommend starting with our Welcome to Peekapak unit!',
    months: [],
    imgURL: `${getPublicMediaRoot()}images/thumbnails/library/middleschoolBanner.png`,
    imgAlt: 'secondary school student waving',
    keyRequired: ['KEY_ALL_LESSON_PLANS'],
    buttons: [
      {
        type: 'videoLink',
        text: 'Watch our how to video',
        linkLocation: 'https://www.youtube.com/watch?v=YE0WoPCa3ec',
        assetId: 'rcy1cyiZVtaffOPZXx9k02secOrLxo0000OVa02WItcxCBA',
      },
    ],
  },
  {
    filterTag: 'middleSchool',
    title: 'Middle School',
    content:
      'As a secondary school teacher with BASIC access to Peekapak, you can explore one lesson from each of our secondary school units, which contains a ready to use lesson plan, a student SELf-assessment for the unit, and a student activity!',
    months: [],
    imgURL: `${getPublicMediaRoot()}images/thumbnails/library/middleschoolBanner.png`,
    imgAlt: 'secondary school student waving',
    keyRequired: ['KEY_BASIC'],
    buttons: [
      {
        type: 'videoLink',
        text: 'Watch our how to video',
        linkLocation: '',
        assetId: 'rcy1cyiZVtaffOPZXx9k02secOrLxo0000OVa02WItcxCBA',
      },
    ],
  },
  {
    filterTag: 'highSchool',
    title: 'High School',
    content:
      'As a Peekapak PRO user, you get full access to Peekapak’s SEL lesson plans, slideshows, and human interest stories. We recommend starting with our ‘Welcome to Peekapak’ unit to help students learn about the importance of SEL, storytelling and working together.',
    months: [],
    imgURL: `${getPublicMediaRoot()}images/thumbnails/library/welcomeBanner-high-school.png`,
    imgAlt: 'secondary school student discussing',
    keyRequired: ['KEY_MANAGE_STUDENTS'],
  },
  {
    filterTag: 'highSchool',
    title: 'High School',
    content:
      'As a Peekapak PRO user, you get full access to Peekapak’s SEL lesson plans, slideshows, and human interest stories. We recommend starting with our ‘Welcome to Peekapak’ unit to help students learn about the importance of SEL, storytelling and working together.',
    months: [],
    imgURL: `${getPublicMediaRoot()}images/thumbnails/library/welcomeBanner-high-school.png`,
    imgAlt: 'secondary school student discussing',
    keyRequired: ['KEY_ALL_LESSON_PLANS'],
  },
  {
    filterTag: 'highSchool',
    title: 'High School',
    content:
      'As a secondary school teacher with BASIC access to Peekapak, you can explore one lesson from each of our secondary school units, which contains a ready to use lesson plan, a student SELf-assessment for the unit, and a student activity!',
    months: [],
    imgURL: `${getPublicMediaRoot()}images/thumbnails/library/welcomeBanner-high-school.png`,
    imgAlt: 'secondary school student discussing',
    keyRequired: ['KEY_BASIC'],
  },
  {
    filterTag: 'supplementary',
    title: 'Supplemental Units',
    content:
      'Peekapak’s supplemental, teacher-led units include our Friendship, Imagination, and Math & Problem Solving mini-units. These are great additions to our core content and teach valuable life skills to students. Please note: These units do not include myPeekaville or parent updates.',
    months: [],
    imgURL: `${getPublicMediaRoot()}images/thumbnails/library/supplementalBanner.png`,
    imgAlt: 'myPeekaville Characters',
    keyRequired: ['KEY_MANAGE_STUDENTS', 'KEY_ALL_LESSON_PLANS'],
  },
  {
    filterTag: 'supplementary',
    title: 'Supplemental Units',
    content:
      'Peekapak’s supplemental, teacher-led units include our Friendship, Imagination, and Math & Problem Solving mini-units. These are great additions to our core content and teach valuable life skills to students. Please note: These units do not include myPeekaville or parent updates.',
    months: [],
    imgURL: `${getPublicMediaRoot()}images/thumbnails/library/supplementalBanner.png`,
    imgAlt: 'myPeekaville Characters',
    keyRequired: ['KEY_BASIC'],
    buttons: [
      {
        type: 'internalLinkPush',
        text: 'Upgrade Now',
        linkLocation: '/activateTrial',
      },
    ],
  },
  {
    filterTag: 'video',
    title: 'Video Lessons',
    content:
      'Our short 7-10 minute video lessons allow classrooms to listen to core SEL lessons through read alouds and discussion questions.',
    months: [],
    imgURL: `${getPublicMediaRoot()}images/thumbnails/library/videoBanner.png`,
    imgAlt: 'myPeekaville Characters',
    keyRequired: ['KEY_MANAGE_STUDENTS'],
  },
  {
    filterTag: 'video',
    title: 'Video Lessons - Available in Peekapak PRO',
    content:
      'Access to our video lessons is a feature provided in our Peekapak PRO subscription. Our short 7-10 minute video lessons allow classrooms to listen to core SEL lessons through read alouds and discussion questions.',
    months: [],
    imgURL: `${getPublicMediaRoot()}images/thumbnails/library/videoBanner.png`,
    imgAlt: 'myPeekaville Characters',
    keyRequired: ['KEY_BASIC', 'KEY_ALL_LESSON_PLANS'],
    buttons: [
      {
        type: 'internalLinkPush',
        text: 'Upgrade Now',
        linkLocation: '/activateTrial',
      },
    ],
  },
  {
    filterTag: 'music',
    title: 'Music Lessons',
    content:
      'Peekapak’s music lessons are a fun way to engage students in SEL, using music. Each unit has a music lesson associated with it. We recommend printing out the lyrics for students to sing along!',
    months: [],
    imgURL: `${getPublicMediaRoot()}images/thumbnails/library/musicBanner.png`,
    imgAlt: 'myPeekaville Characters',
    keyRequired: ['KEY_MANAGE_STUDENTS'],
    buttons: [
      {
        type: 'internalLinkWindow',
        text: 'Print Lyrics',
        linkLocation: `${getPublicMediaRoot()}resources/lyrics-combined.pdf`,
      },
    ],
  },
  {
    filterTag: 'music',
    title: 'Music Lessons - Available in Peekapak PRO',
    content:
      'Upgrade to a Peekapak PRO subscription to access our music lessons. Peekapak’s music lessons are a fun way to engage students in SEL, using music. There is a music lesson for each of our core elementary units. We recommend printing out the lyrics for students to sing along!',
    months: [],
    imgURL: `${getPublicMediaRoot()}images/thumbnails/library/musicBanner.png`,
    imgAlt: 'myPeekaville Characters',
    keyRequired: ['KEY_ALL_LESSON_PLANS', 'KEY_BASIC'],
    buttons: [
      {
        type: 'internalLinkPush',
        text: 'Upgrade Now',
        linkLocation: '/activateTrial',
      },
    ],
  },
  {
    filterTag: 'howTo',
    level: ['EL'],
    title: 'How To Resources',
    content:
      'To help you get started with Peekapak and myPeekaville, review our help resources we’ve listed here. This includes step by step videos, how to guides, recommended scope and sequences and more!',
    months: [],
    imgURL: `${getPublicMediaRoot()}images/thumbnails/library/guidesBanner.png`,
    imgAlt: 'myPeekaville Characters',
    keyRequired: ['KEY_MANAGE_STUDENTS', 'KEY_ALL_LESSON_PLANS', 'KEY_BASIC'],
    buttons: [
      {
        type: 'internalLinkWindow',
        text: 'Getting Started',
        linkLocation: Documents.getStartedPeekapakPKto5,
      },
      {
        type: 'unitLink',
        text: 'Intro to myPeekaville',
        linkLocation: '',
      },
    ],
  },
  {
    filterTag: 'howTo',
    title: 'How To Resources',
    level: ['MS'],
    content:
      'To help you get started with Peekapak, review our help resources we’ve listed here. This includes step by step videos, how to guides, recommended scope and sequences and more!',
    months: [],
    imgURL: `${getPublicMediaRoot()}images/thumbnails/library/guidesBanner-middle-school.png`,
    imgAlt: '',
    keyRequired: ['KEY_MANAGE_STUDENTS', 'KEY_ALL_LESSON_PLANS', 'KEY_BASIC'],
    buttons: [
      {
        type: 'internalLinkWindow',
        text: 'Getting Started',
        linkLocation: Documents.getStartedPeekapakGrade6to8,
      },
    ],
  },
  {
    filterTag: 'howTo',
    level: ['HS'],
    title: 'How To Resources',
    content:
      'To help you get started with Peekapak, review our help resources we’ve listed here. This includes step by step videos, how to guides, recommended scope and sequences and more!',
    months: [],
    imgURL: `${getPublicMediaRoot()}images/thumbnails/library/guidesBanner-high-school.png`,
    imgAlt: 'high school student',
    keyRequired: ['KEY_MANAGE_STUDENTS', 'KEY_ALL_LESSON_PLANS', 'KEY_BASIC'],
  },
  {
    filterTag: 'posters',
    level: ['EL'],
    title: 'Posters',
    content:
      'Download and print free Peekapak posters aligned to each unit! We recommend printing the unit poster for the unit you’re working on, and displaying it on your classroom wall.',
    months: [],
    imgURL: `${getPublicMediaRoot()}images/thumbnails/library/postersBanner.png`,
    imgAlt: 'myPeekaville Characters',
    keyRequired: ['KEY_MANAGE_STUDENTS', 'KEY_ALL_LESSON_PLANS'],
  },
  {
    filterTag: 'posters',
    level: ['EL'],
    title: 'Posters',
    content:
      'Download and print free Peekapak posters aligned to each unit! We recommend printing the unit poster for the unit you’re working on, and displaying it on your classroom wall.',
    months: [],
    imgURL: `${getPublicMediaRoot()}images/thumbnails/library/postersBanner.png`,
    imgAlt: 'myPeekaville Characters',
    keyRequired: ['KEY_BASIC'],
    buttons: [
      {
        type: 'internalLinkPush',
        text: 'Upgrade Now',
        linkLocation: '/activateTrial',
      },
    ],
  },
  {
    filterTag: 'posters',
    level: ['MS'],
    title: 'Posters',
    content:
      'Download and print free Peekapak posters aligned to each unit! We recommend printing the unit poster for the unit you’re working on, and displaying it on your classroom wall.',
    months: [],
    imgURL: `${getPublicMediaRoot()}images/thumbnails/library/postersBanner-middle-school.png`,
    imgAlt: '',
    keyRequired: ['KEY_MANAGE_STUDENTS', 'KEY_ALL_LESSON_PLANS'],
  },
  {
    filterTag: 'posters',
    level: ['MS'],
    title: 'Posters',
    content:
      'Download and print free Peekapak posters aligned to each unit! We recommend printing the unit poster for the unit you’re working on, and displaying it on your classroom wall.',
    months: [],
    imgURL: `${getPublicMediaRoot()}images/thumbnails/library/postersBanner-middle-school.png`,
    imgAlt: '',
    keyRequired: ['KEY_BASIC'],
    buttons: [
      {
        type: 'internalLinkPush',
        text: 'Upgrade Now',
        linkLocation: '/activateTrial',
      },
    ],
  },
  {
    filterTag: 'posters',
    level: ['HS'],
    title: 'Posters',
    content:
      'Download and print free Peekapak posters aligned to each unit! We recommend printing the unit poster for the unit you’re working on, and displaying it on your classroom wall.',
    months: [],
    imgURL: `${getPublicMediaRoot()}images/thumbnails/library/postersBanner-high-school.png`,
    imgAlt: '',
    keyRequired: ['KEY_MANAGE_STUDENTS', 'KEY_ALL_LESSON_PLANS'],
  },
  {
    filterTag: 'posters',
    level: ['HS'],
    title: 'Posters',
    content:
      'Download and print free Peekapak posters aligned to each unit! We recommend printing the unit poster for the unit you’re working on, and displaying it on your classroom wall.',
    months: [],
    imgURL: `${getPublicMediaRoot()}images/thumbnails/library/postersBanner-high-school.png`,
    imgAlt: '',
    keyRequired: ['KEY_BASIC'],
    buttons: [
      {
        type: 'internalLinkPush',
        text: 'Upgrade Now',
        linkLocation: '/activateTrial',
      },
    ],
  },
  {
    filterTag: 'extras',
    level: ['EL'],
    title: 'Extras',
    content:
      'Excited to bring to life the world of Peekaville in your classroom? We have lots of printable scenes from our book series and cut out characters to help students create their own stories and be part of the fun.',
    months: [],
    imgURL: `${getPublicMediaRoot()}images/thumbnails/library/extrasBanner.png`,
    imgAlt: 'myPeekaville Characters',
    keyRequired: ['KEY_MANAGE_STUDENTS', 'KEY_ALL_LESSON_PLANS'],
  },
  {
    filterTag: 'extras',
    level: ['EL'],
    title: 'Extras',
    content:
      'Excited to bring to life the world of Peekaville in your classroom? We have lots of printable scenes from our book series and cut out characters to help students create their own stories and be part of the fun.',
    months: [],
    imgURL: `${getPublicMediaRoot()}images/thumbnails/library/extrasBanner.png`,
    imgAlt: 'myPeekaville Characters',
    keyRequired: ['KEY_BASIC'],
    buttons: [
      {
        type: 'internalLinkPush',
        text: 'Upgrade Now',
        linkLocation: '/activateTrial',
      },
    ],
  },
  {
    filterTag: 'extras',
    level: ['MS'],
    title: 'Extras',
    content:
      'Peekapak wants to help you infuse SEL in your secondary classroom. Leverage our student achievement certificates, activity calendars and journal templates for extra SEL fun!',
    months: [],
    imgURL: `${getPublicMediaRoot()}images/thumbnails/library/extrasBanner-middle-school.png`,
    imgAlt: '',
    keyRequired: ['KEY_MANAGE_STUDENTS', 'KEY_ALL_LESSON_PLANS'],
  },
  {
    filterTag: 'extras',
    level: ['MS'],
    title: 'Extras',
    content:
      'Peekapak wants to help you infuse SEL in your secondary classroom. Leverage our student achievement certificates, activity calendars and journal templates for extra SEL fun!',
    months: [],
    imgURL: `${getPublicMediaRoot()}images/thumbnails/library/extrasBanner-middle-school.png`,
    imgAlt: '',
    keyRequired: ['KEY_BASIC'],
    buttons: [
      {
        type: 'internalLinkPush',
        text: 'Upgrade Now',
        linkLocation: '/activateTrial',
      },
    ],
  },
  {
    filterTag: 'extras',
    level: ['HS'],
    title: 'Extras',
    content:
      'Peekapak wants to help you infuse SEL in your secondary classroom. Leverage our student achievement certificates, activity calendars and journal templates for extra SEL fun!',
    months: [],
    imgURL: `${getPublicMediaRoot()}images/thumbnails/library/extrasBanner-high-school.png`,
    imgAlt: '',
    keyRequired: ['KEY_MANAGE_STUDENTS', 'KEY_ALL_LESSON_PLANS'],
  },
  {
    filterTag: 'extras',
    level: ['HS'],
    title: 'Extras',
    content:
      'Peekapak wants to help you infuse SEL in your secondary classroom. Leverage our student achievement certificates, activity calendars and journal templates for extra SEL fun!',
    months: [],
    imgURL: `${getPublicMediaRoot()}images/thumbnails/library/extrasBanner-high-school.png`,
    imgAlt: '',
    keyRequired: ['KEY_BASIC'],
    buttons: [
      {
        type: 'internalLinkPush',
        text: 'Upgrade Now',
        linkLocation: '/activateTrial',
      },
    ],
  },
];

// Function to get banners with license-level customization
export const getBanners = (userProfile?: UserProfileType | null): LibraryBannerType[] => {
  const licenseLevel = userProfile?.licenseLevel;

  if (!licenseLevel) {
    return banners; // Return default banners if no license level
  }

  // Get all custom banners for this license level
  const customBannersForLicense = CUSTOM_BANNER_CONFIGS.filter((config) => config.licenseLevel === licenseLevel);

  if (customBannersForLicense.length === 0) {
    return banners; // Return default banners if no custom configuration
  }

  // Create a map of custom banners by filterTag (and level if present)
  const customBannerMap = new Map<string, LibraryBannerType>();
  customBannersForLicense.forEach((config) => {
    const key = config.level ? `${config.filterTag}_${config.level}` : config.filterTag;
    customBannerMap.set(key, config.banner);
  });

  // Replace matching banners with custom ones, keep others as default
  const result = banners.map((banner) => {
    const key = banner.level ? `${banner.filterTag}_${banner.level}` : banner.filterTag;
    return customBannerMap.get(key) || banner;
  });

  // Also add any custom banners that don't have default counterparts
  customBannersForLicense.forEach((config) => {
    const key = config.level ? `${config.filterTag}_${config.level}` : config.filterTag;
    const existsInDefaults = banners.some((banner) => {
      const bannerKey = banner.level ? `${banner.filterTag}_${banner.level}` : banner.filterTag;
      return bannerKey === key;
    });

    if (!existsInDefaults) {
      result.push(config.banner);
    }
  });

  // For Takween, ensure the custom banners are properly included
  // The Takween banner should replace ALL default banners with filterTag: ''
  if (licenseLevel === 'TAKWEEN') {
    // Remove all default banners with filterTag: '' (regardless of level)
    const filteredResult = result.filter((b) => !(b.filterTag === '' && !b.logoURLs));

    // Find the Takween default banner (has logoURLs)
    const defaultTakweenBanner = customBannersForLicense.find((config) => config.filterTag === '')?.banner;

    if (defaultTakweenBanner) {
      // Add Takween banner at the beginning
      return [defaultTakweenBanner, ...filteredResult];
    }
  }

  return result;
};
